/**
 * The welcome-back panel: a modal shown on load when offline progress was
 * worth mentioning. Closes on the button, Escape, or a click on the backdrop.
 */

import { formatDuration, formatEggs } from '../game/numbers';
import type { OfflineResult } from '../game/save';

export interface WelcomeView {
  show(result: OfflineResult, onClose?: () => void): void;
  readonly open: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createWelcomeView(root: HTMLElement): WelcomeView {
  let open = false;
  let closeHandler: (() => void) | undefined;

  const backdrop = el('div', 'modal');
  backdrop.hidden = true;
  const panel = el('div', 'modal__panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  const title = el('h2', 'modal__title', 'Welcome back!');
  const body = el('p', 'modal__body');
  const note = el('p', 'modal__note');
  const button = el('button', 'modal__button', 'Collect');
  button.type = 'button';
  panel.append(title, body, note, button);
  backdrop.append(panel);
  root.append(backdrop);

  function close(): void {
    if (!open) return;
    open = false;
    backdrop.hidden = true;
    closeHandler?.();
    closeHandler = undefined;
  }

  button.addEventListener('click', close);
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) close();
  });
  window.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') close();
  });

  return {
    get open() {
      return open;
    },
    show(result, onClose) {
      closeHandler = onClose;
      body.textContent =
        `While you were away for ${formatDuration(result.seconds)}, ` +
        `your dinosaurs laid ${formatEggs(result.eggs)} eggs ` +
        `(an away rate of ${Math.round(result.rate * 100)}% of eggs per second).`;
      note.textContent = result.capped
        ? `Time away counts for up to ${Math.round(result.cap / 3600)} hours. Come back sooner for more!`
        : '';
      note.hidden = !result.capped;
      open = true;
      backdrop.hidden = false;
      button.focus();
    },
  };
}
